
import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { TrendingUp } from "lucide-react";
import { Avatar, AvatarImage } from "@/components/ui/avatar";
import { WalletButton } from "@/wallet";

interface JoinSectionProps {
  onConnectWallet: () => void;
}

const JoinSection = ({ onConnectWallet }: JoinSectionProps) => {
  const [price, setPrice] = useState("$0.000321");
  const [change, setChange] = useState("+14.2%");
  const [isPositive, setIsPositive] = useState(true);

  // Simulate price changes
  useState(() => {
    const interval = setInterval(() => {
      const newPrice = `$0.0003${Math.floor(Math.random() * 10)}${Math.floor(Math.random() * 10)}`;
      const newChange = Math.random() > 0.3;
      const changePercent = (Math.random() * 20).toFixed(1);
      
      setPrice(newPrice);
      setChange(`${newChange ? '+' : '-'}${changePercent}%`);
      setIsPositive(newChange);
    }, 5000);
    
    return () => clearInterval(interval);
  });

  const steps = [
    {
      number: "01",
      title: "Create a Wallet",
      description: "Download Phantom or Solflare wallet from the app store or browser extension"
    },
    {
      number: "02",
      title: "Buy SOL",
      description: "Purchase SOL from your favorite exchange or directly in your wallet"
    },
    {
      number: "03",
      title: "Connect Wallet",
      description: "Connect your wallet to our website with the button below"
    },
    {
      number: "04",
      title: "Swap for $POP",
      description: "Use the quick swap feature to get your $POP tokens"
    }
  ];

  return (
    <section id="join" className="py-24 bg-black relative">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-bold mb-16 text-center">
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-orange-400 to-orange-600">
            How to Catch $POP
          </span>
        </h2>

        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Steps */}
            <div className="md:col-span-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {steps.map((step, index) => (
                  <Card key={index} className="bg-black/60 border-orange-500/20 shadow-lg overflow-hidden">
                    <CardContent className="p-6">
                      <span className="inline-block px-3 py-1 mb-4 text-xs font-semibold bg-orange-500/10 text-orange-400 rounded-full">
                        STEP {step.number}
                      </span>
                      <h3 className="text-xl font-bold mb-3 text-white">{step.title}</h3>
                      <p className="text-gray-400 text-sm">{step.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
              
              <div className="mt-10 flex justify-center">
                <WalletButton 
                  size="lg" 
                  className="py-6 px-8 text-lg"
                />
              </div>
            </div>
            
            {/* Live price ticker */}
            <div className="flex">
              <Card className="bg-black/60 border-orange-500/20 shadow-lg w-full">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-6 text-white border-b border-white/10 pb-4">
                    Tides & Trades
                  </h3>
                  
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-orange-500 rounded-full mr-3 flex items-center justify-center">
                        <Avatar className="h-full w-full">
                          <AvatarImage src="/lovable-uploads/d878e94b-d7f7-4ca0-b19f-17aed49022c0.png" alt="PopCoin mascot" />
                        </Avatar>
                      </div>
                      <div>
                        <p className="font-bold text-white">$POP</p>
                        <p className="text-xs text-gray-400">PopCoin</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-end justify-between mt-8">
                    <div>
                      <p className="text-3xl font-bold text-white">{price}</p>
                    </div>
                    <div className={`flex items-center ${isPositive ? 'text-green-500' : 'text-red-500'}`}>
                      <TrendingUp size={16} className="mr-1" />
                      <p className="font-bold">{change}</p>
                    </div>
                  </div>
                  
                  <div className="mt-10 pt-6 border-t border-white/10">
                    <div className="flex justify-between text-sm mb-2">
                      <p className="text-gray-400">Market Cap</p>
                      <p className="text-white">$2.4M</p>
                    </div>
                    <div className="flex justify-between text-sm mb-2">
                      <p className="text-gray-400">Volume (24h)</p>
                      <p className="text-white">$532K</p>
                    </div>
                    <div className="flex justify-between text-sm">
                      <p className="text-gray-400">Circulating Supply</p>
                      <p className="text-white">64.1M $POP</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
      
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-64 h-64 bg-orange-500/5 rounded-full blur-3xl -z-10"></div>
      <div className="absolute bottom-0 right-0 w-80 h-80 bg-blue-900/5 rounded-full blur-3xl -z-10"></div>
    </section>
  );
};

export default JoinSection;
